<template>
  <v-container fluid class="agenda agenda--customer">
    <page-skeleton v-if="loading" variant="calendar" class="mt-4" />

    <template v-else>
      <header class="agenda__head">
        <div>
          <div class="agenda__eyebrow">{{ $t("agenda.student") }}</div>
          <h1 class="agenda__title">{{ $t("agenda.title") }}</h1>
          <p class="agenda__sub">
            {{ $t("agenda.customerSub") }}
          </p>
        </div>
      </header>

      <div class="agenda__panel">
        <AppCalendar
          :items="calendarEvents"
          @adiar="adiar"
          @participate-general-class="confirmGeneralClassParticipation"
          @decline-general-class="declineGeneralClassParticipation"
        />
      </div>

      <GeneralClassEventsList
        :items="generalClasses"
        :busy-id="busyGeneralClassId"
        @participate="confirmGeneralClassParticipation"
        @decline="declineGeneralClassParticipation"
      />
    </template>
  </v-container>
</template>

<script>
import AppCalendar from "@/components/calendar/calendar";
import GeneralClassEventsList from "@/components/calendar/GeneralClassEventsList";
import customerApi from "@/shared/pitchCustomerApi";

export default {
  name: "CustomerCalendar",
  components: { AppCalendar, GeneralClassEventsList },
  created() {
    this.loadData();
  },
  data() {
    return {
      loading: true,
      events: [],
      generalClasses: [],
      busyGeneralClassId: null,
    };
  },
  computed: {
    // Card 86aknb3ra: o aluno entrava no link da conversação achando que era a
    // aula com o professor. O calendário mostra as aulas da turma e só as
    // conversações/monitorias que ele confirmou (ou em que o professor o
    // incluiu na chamada); as demais ficam na lista abaixo.
    calendarEvents() {
      return this.events.filter(
        (event) =>
          !event.isGeneralClass ||
          event.participationConfirmed ||
          event.addedByTeacher
      );
    },
  },
  methods: {
    loadData() {
      // Recarga depois de participar/recusar não volta ao spinner: a lista fica
      // no lugar e só troca o estado da linha.
      if (!this.events.length) this.loading = true;
      return Promise.all([
        customerApi.calendar(),
        customerApi.generalClasses().catch(() => ({ data: { events: [] } })),
      ])
        .then(([calendar, generalClasses]) => {
          this.events = calendar.data.events;
          this.generalClasses = generalClasses.data.events || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    adiar(item) {
      let request;
      const { action } = item;
      switch (action) {
        case "cancel": {
          if (window.confirm(this.$t("agenda.confirmCancel"))) {
            request = customerApi.cancelarAula(item.item.id);
          }
          break;
        }
        case "next": {
          request = customerApi.adiarParaProxima(item.item.id);
          break;
        }
        case "other": {
          request = customerApi.postergate(item.item.id, item.item.data);
          break;
        }
        default:
          return;
      }
      request.then(() => {
        this.$root.$emit("display-alert", {
          text: this.$t("agenda.changeSent"),
          type: 0,
        });
        this.loadData();
      });
    },
    async confirmGeneralClassParticipation(id) {
      this.busyGeneralClassId = id;
      try {
        const res = await customerApi.participateInGeneralClass(id);

        this.$root.$emit("display-alert", {
          text: res.data?.alreadyConfirmed
            ? this.$t("agenda.alreadyConfirmed")
            : this.$t("agenda.confirmed"),
          type: 0,
        });

        this.loadData();
      } catch (err) {
        this.$root.$emit("display-alert", {
          text: err?.response?.data?.message || this.$t("agenda.confirmFailed"),
          type: 1,
        });
      } finally {
        this.busyGeneralClassId = null;
      }
    },
    async declineGeneralClassParticipation(id) {
      this.busyGeneralClassId = id;
      try {
        await customerApi.declineGeneralClass(id);

        this.$root.$emit("display-alert", {
          text: this.$t("agenda.declined"),
          type: 0,
        });

        this.loadData();
      } catch (err) {
        this.$root.$emit("display-alert", {
          text: err?.response?.data?.message || this.$t("agenda.declineFailed"),
          type: 1,
        });
      } finally {
        this.busyGeneralClassId = null;
      }
    },
  },
};
</script>

<style scoped>
.agenda {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-pink, #e8276f);
  padding: 8px 4px 40px;
  color: var(--ink);
}

.agenda__head {
  margin: 12px 0 22px;
}
.agenda__eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}
.agenda__title {
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.agenda__sub {
  margin: 8px 0 0;
  max-width: 48ch;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.5;
}
</style>
